import { useState } from "react";
import { useMutation } from "@apollo/client";
import { TaskStageValues } from "@/types";
import type { CRMTask, TaskStage } from "@/types";
import { showSnackbar } from "@/lib/snackbarUtils";
import { UPDATE_TASK_MUTATION, type UpdateTaskResponse } from "./mutations";
import { evictTasksCache } from "../queries";
import useAmplitude from "@/hooks/useAmplitude";
import { TASK_ANALYTICS_EVENTS, getTaskAnalyticsData, type TaskAnalyticsSource } from "../_utils/amplitude";

type UseTaskStageActionsOptions = {
  task?: CRMTask;
  source?: TaskAnalyticsSource;
};

export default function useTaskStageActions(taskId: string, options: UseTaskStageActionsOptions = {}) {
  const [error, setError] = useState<string | null>(null);
  const { trackEvent } = useAmplitude();

  const [updateTask, { loading }] = useMutation<UpdateTaskResponse>(UPDATE_TASK_MUTATION, {
    update: evictTasksCache
  });

  async function updateStage(stage: TaskStage, successMessage: string, failureMessage: string) {
    if (loading) return false;
    setError(null);

    try {
      const result = await updateTask({
        variables: { input: { id: taskId, stage } }
      });

      if (result.data?.updateTask?.success) {
        if (options.source) {
          trackEvent(TASK_ANALYTICS_EVENTS.stageChanged, {
            ...(options.task
              ? getTaskAnalyticsData(options.task, options.source, { includeStage: false })
              : { taskId, source: options.source }),
            fromStage: options.task?.stage,
            toStage: stage
          });
        }
        showSnackbar(successMessage);
        return true;
      }

      setError(result.data?.updateTask?.message || failureMessage);
      return false;
    } catch (err) {
      const message = err instanceof Error ? err.message : `${failureMessage}. Please try again.`;
      setError(message);
      return false;
    }
  }

  const completeTask = () => updateStage(TaskStageValues.COMPLETE, "Task completed", "Failed to complete task");

  const markIncomplete = () => updateStage(TaskStageValues.OPEN, "Task marked incomplete", "Failed to reopen task");

  const markNotDoing = () =>
    updateStage(TaskStageValues.NOT_DOING, "Task marked as not doing", "Failed to update task");

  const reopenTask = () => updateStage(TaskStageValues.OPEN, "Task reopened", "Failed to reopen task");

  return { completeTask, markIncomplete, markNotDoing, reopenTask, loading, error };
}
